import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import { useHistory } from 'react-router';
import { CardContent, Divider, Typography } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import Alert from '@material-ui/lab/Alert';
import AlertTitle from '@material-ui/lab/AlertTitle';
import { useItems } from '../../../hooks/atoms/items-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import { useItem } from '../../../hooks/atoms/item-hook';
import BasicField from '../../basic/BasicField';
import AppLoader from '../../basic/AppLoader';
import AppDropdownButton from '../../basic/AppDropdownButton';
import usePermissions from '../../../hooks/permissions-hooks';
import AppTable from '../../basic/AppTable';
import actions from '../../../roles-actions-permissions/actions';
import VersionSelectTable from './VersionSelectTable';
import AssociationReportsDetails from './AssociationReportsDetails';
import VersionSimulations from '../edag-simulations/VersionSimulations';
import { useDialog } from '../../../dialog-provider-hooks';
import AppButtonMinimal from '../../basic/AppButtonMinimal';
import getTheme from '../../../theme/theme';
import { CollectionItemHeader } from '../../layout';

const tableColumns = [
  { id: 1, label: 'ID', field: 'id' },
  { id: 2, label: 'Name', field: 'name' },
  { id: 3, label: 'Status', field: 'roadmap_status' },
  { id: 4, label: 'Device', field: 'device_id.name' },
  { id: 5, label: 'Race', field: 'race_id.name' },
];

const ThematicPackageTableForAssociations = () => {
  const { hasPermissions } = usePermissions();

  const history = useHistory();

  const {
    items: thematicPackages,
    itemsLoading: thematicPackagesLoading,
    itemsError: thematicPackagesError,
    pagination,
  } = useItems(CollectionKeys.THEMATIC_PACKAGES);

  const handleViewAction = (event, id) => {
    history.push({
      pathname: '/association-reports',
      search: `?thematic-package-id=${ id }`,
    });
  };

  if (thematicPackagesError) {
    return (
      <span>
        Error:
        {thematicPackagesError.message}
      </span>
    );
  }

  return (
    <AppTable
      columns={ tableColumns }
      paginationProps={ pagination }
      loading={ thematicPackagesLoading }
      data={ thematicPackages }
      ViewProp={ { handleView: handleViewAction, disabledView: !hasPermissions(actions.VIEW_ASSOCIATION_REPORTS_PAGE) } }
    />
  );
};

const VersionsPerThematicPackage = (
  {
    thematicPackageId,
  },
) => {
  const [ selectedTrait, setSelectedTrait ] = useState(null);

  const {
    item: thematicPackage,
    itemIsLoading: thematicPackageLoading,
  } = useItem(CollectionKeys.THEMATIC_PACKAGES, thematicPackageId,
    {
      queryOptions: {
        enabled: !!thematicPackageId,
      },
    });

  const { race_id: race, device_id: device } = thematicPackage || {};

  const {
    items: thematicPackageTraits,
    itemsLoading: thematicPackageTraitsLoading,
  } = useItems(CollectionKeys.THEMATIC_PACKAGES_TRAITS,
    {
      queryOptions: {
        enabled: !!thematicPackageId,
      },
      filterQuery: { _limit: -1, thematicpackage_id: thematicPackageId },
    });

  if (thematicPackageLoading) {
    return <AppLoader />;
  }

  if (!race || !device) {
    return <p>The selected thematic package has not yet been associated with a race or a device</p>;
  }

  return (
    <>
      <br />
      <CollectionItemHeader
        title={ `Name: ${ thematicPackage.name }` }
        subtitle={ `Description: ${ thematicPackage.description }` }
      />
      <br />
      <Grid container spacing={ 1 }>
        <Grid item xs={ 12 } md={ 3 }>
          <BasicField
            disabled
            title='Race'
            field={ race.name }
          />
        </Grid>
        <Grid item xs={ 12 } md={ 3 }>
          <BasicField
            disabled
            title='Device'
            field={ device.name }
          />
        </Grid>
        <Grid item xs={ 12 } md={ 6 }>
          <AppDropdownButton
            options={ thematicPackageTraits?.map((tpt) => { return tpt.trait_id; }) }
            loading={ thematicPackageTraitsLoading }
            title='Thematic package traits'
            onChange={ setSelectedTrait }
            value={ selectedTrait }
            disabled={ thematicPackageTraitsLoading }
          />
        </Grid>
        {
          !!race && !!device && selectedTrait && (
            <Grid item container xs={ 12 }>
              <Grid item xs={ 12 }>
                <Typography variant='h6' style={ { paddingBottom: '12px' } }>
                  Versions
                </Typography>
              </Grid>
              <Grid item xs={ 12 }>
                <VersionSelectTable
                  trait={ selectedTrait }
                  race={ race }
                  device={ device }
                />
              </Grid>
            </Grid>
          )
        }

      </Grid>
    </>
  );
};

function AssociationReportsVersion(
  {
    versionId,
  },
) {
  const {
    item: version,
    itemIsLoading: versionLoading,
    updateItem,
  } = useItem(CollectionKeys.VERSIONS, versionId);

  const rejectVersion = (reason = '') => {
    return updateItem({ status: 'rejected', reason });
  };

  const activateVersion = () => {
    return updateItem({ status: 'pending' });
  };

  const { openDialog } = useDialog();

  const openRejectVersionDialog = () => {
    openDialog({
      title: 'Reject version ?',
      body({ state, setState }) {
        return (
          <>
            <p>Are you sure you want to reject this version ?</p>
            <BasicField
              title='Reason:'
              field={ state.reason || '' }
              onChange={ (f) => {
                return setState((prevState) => {
                  return { ...prevState, reason: f };
                });
              } }
              multiline
            />
          </>
        );
      },
      onSubmit(state) {
        return rejectVersion(state.reason?.trim());
      },
    });
  };

  const openActivateVersionDialog = () => {
    openDialog({
      title: 'Activate version ?',
      body() {
        return (
          <p>Are you sure you want to activate this version ?</p>
        );
      },
      onSubmit() {
        return activateVersion();
      },
    });
  };

  if (versionLoading) {
    return <AppLoader />;
  }

  if (!version) {
    return <p>Something went wrong</p>;
  }

  return (
    <Grid container spacing={ 4 }>
      <Grid container item xs={ 12 } spacing={ 3 }>
        <Grid item xs={ 4 }>
          <Card>
            <CardContent>
              <Grid item xs={ 12 }>
                <BasicField
                  disabled
                  title='Version ID'
                  field={ version.id }
                />
                <BasicField
                  disabled
                  title='Covariance ID'
                  field={ version.covariance_id }
                />
                <BasicField
                  disabled
                  title='Trait'
                  field={ version.trait.name }
                />
                <BasicField
                  disabled
                  title='Race'
                  field={ version.race.name }
                />
                <BasicField
                  disabled
                  title='Device'
                  field={ version.device.name }
                />
                <BasicField
                  disabled
                  title='Timestamp'
                  field={ version.timestamp }
                />
                <Grid item>
                  <Divider style={ { margin: getTheme().spacing(1, 0) } } />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={ 8 }>
          {'rejected' === version.status && (
            <Grid container spacing={ 2 }>
              <Grid item xs={ 12 }>
                <Alert severity='error'>
                  <AlertTitle>This version is rejected.</AlertTitle>
                  <strong>{`Last updated: ${ version.updated_at }`}</strong>
                  <br />
                  <br />
                  <strong>{`Rejection reason: ${ version.reason }`}</strong>
                  <br />
                  You may undo this action by reactivating the version.
                </Alert>
              </Grid>
              <Grid item>
                <AppButtonMinimal
                  text='Activate version'
                  color={ getTheme().palette.info.main }
                  onClick={ () => { return openActivateVersionDialog(); } }
                />
              </Grid>
            </Grid>
          )}
          {'pending' === version.status && (
            <Grid container spacing={ 2 }>
              <Grid item xs={ 12 }>
                <Alert severity='info'>
                  <AlertTitle>This version is in progress.</AlertTitle>
                  <strong>{`Last updated: ${ version.updated_at }`}</strong>
                  <br />
                  <br />
                  You may run simulations on associations reports related to this version.
                  <br />
                  If the simulation results are satisfying you can approve this version by approving the corresponding simulation
                  <br />
                  If none of the simulations are satisfying you can reject this version
                </Alert>
              </Grid>
              <Grid item>
                <AppButtonMinimal
                  text='Reject version'
                  color={ getTheme().palette.error.main }
                  onClick={ () => { return openRejectVersionDialog(); } }
                />
              </Grid>
            </Grid>
          )}
          {'accepted' === version.status && (
            <Grid container>
              <Grid item xs={ 12 }>
                <Alert severity='success'>
                  <AlertTitle>This version has been approved</AlertTitle>
                  <strong>{`Last updated: ${ version.updated_at }`}</strong>
                  <br />
                  <br />
                </Alert>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      { 'pending' === version.status
        && (
          <>
            <Grid item xs={ 12 }>
              <AssociationReportsDetails version={ version } />
            </Grid>
            <Grid item xs={ 12 }>
              <VersionSimulations version={ version } />
            </Grid>
          </>
        )}
    </Grid>
  );
}

const AssociationReportsResolver = () => {
  const location = useLocation();

  const { search } = location;
  const thematicPackageId = new URLSearchParams(search).get('thematic-package-id');
  const versionId = new URLSearchParams(search).get('version-id');

  if (thematicPackageId) {
    return <VersionsPerThematicPackage thematicPackageId={ thematicPackageId } />;
  }

  if (versionId) {
    return <AssociationReportsVersion versionId={ versionId } />;
  }

  return <ThematicPackageTableForAssociations />;
};
export default AssociationReportsResolver;

